import React, { useState, useContext, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation, useNavigate } from "react-router-dom";
import { Box, TextField, FormControl, FormHelperText } from "@mui/material";
import Question from "../../components/Question";
import ContinueButton from "../../components/ContinueButton";
import { setOther as updateOther } from '../../redux/userSlice';
import { MinorUserContext } from "../../context/UserNameProvider";
import { callBackendApiPost } from '../../lib/api';
import { formatDataNoPhotosReturning } from '../../lib/formatting';
import { textAreaSx } from '../../lib/uiformatting';
import logDrtEvent from '../../lib/logging';

export default function AnythingElseToKnow() {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();

  const { returning = false, insuranceChanged = false, onFile = false } = location.state || {};
  const user = useSelector((state) => state.user);
  const insurance = useSelector((state) => state.insurance);
  const stripe = useSelector((state) => state.stripe);

  const [other, setOther] = useState(user.other);
  const [errorMessage, setErrorMessage] = useState("");
  const [processing, setProcessing] = useState(false);
  const [cardFront, setCardFront] = useState(null);
  const [cardBack, setCardBack] = useState(null);
  const { isMinor, firstName } = useContext(MinorUserContext);

  const variant = localStorage.getItem('ab_variant');
  const showApptBooking = variant === 'show_appt_booking_in_ret_intake';

  const submitButton = showApptBooking ? 'Continue' : 'Submit';

  useEffect(() => {
    async function processCardFront() {
      if (!insurance.cardFront || !insurance.cardFront.length) return;
      try {
        const dataURL = await toDataURL(insurance.cardFront[0]);
        if (dataURL) {
          setCardFront({ dataURL, fileName: insurance.cardFront[0].name });
        }
      } catch (error) {
        console.error("Error processing card front:", error);
      }
    }
    processCardFront();
  }, [insurance.cardFront]);

  useEffect(() => {
    async function processCardBack() {
      if (!insurance.cardBack || !insurance.cardBack.length) return;
      try {
        const dataURL = await toDataURL(insurance.cardBack[0]);
        if (dataURL) {
          setCardBack({ dataURL, fileName: insurance.cardBack[0].name });
        }
      } catch (error) {
        console.error("Error processing card back:", error);
      }
    }
    processCardBack();
  }, [insurance.cardBack]);

  async function toDataURL(file) {
    return new Promise((resolve, reject) => {
      if (!(file instanceof Blob)) {
        console.warn("Invalid file object:", file);
        resolve(null);
        return;
      }
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  const handleSubmit = async () => {
    try {
      setProcessing(true);
      dispatch(updateOther(other));
      const data = formatDataNoPhotosReturning(user, insurance, stripe);
      data.insurance.cardFront = cardFront;
      data.insurance.cardBack = cardBack;
      data.currentSymptoms = other;
      logDrtEvent(`[returning] returning intake submission started for patient: ${data?.patientID} and case: ${data?.caseID}`);
      const response = await callBackendApiPost('/api/v1/submit-returning', data);
      if (response.ok || response.success) {
        if (user.portal) {
            navigate('/case-list');
        } else if (user.isIsotretinoin) {
            navigate('/isotretinoin-thank-you');
        } else {
            if (user.payMethod === "insurance") {
                if (showApptBooking) {
                    const patient = {
                        id: user.patientID,
                        state: user.address.state,
                        insurance: {
                            payerName: insurance.insurer
                        }
                    };
                    navigate("/appointment-whats-next", { state: { patient } });
                } else {
                    navigate('/thank-you');
                }
            } else {
                navigate('/self-thank-you');
            }
        }
      } else {
        logDrtEvent(`Error while submitting returning intake with response: ${response}`);
        setErrorMessage("There was an error submitting your information. Please try again, and contact support (call or text 781-650-4492) if the issue persists.");
        setProcessing(false);
      }
    } catch (err) {
      logDrtEvent(`Error while submitting returning intake with error: ${err}`);
      setErrorMessage("An unexpected error occurred. Please try again later.");
      setProcessing(false);
    }
  };

  const handleBack = () => {
    if (user.payMethod === "self") {
      navigate('/payment', { state: { returning: true } });
    } else if (user.payMethod === "insurance") {
      if (user.skippedInsurance) {
        navigate('/insurance/method', { state: { returning: true } });
      } else if (onFile) {
        navigate('/insurance-options', { state: { returning: true } });
      } else if (insuranceChanged) {
        navigate('/insurance-confirm', { state: { returning: true } });
      } else {
        navigate('/insurance/provider', { state: { returning: true } });
      }
    }
  };


  return (
    <Question
      onBack={handleBack}
      title="Finally, describe your dermatology concern."
      subtitle={
        `How ${isMinor ? (firstName + "'s") : "your"} concern looks/feels. Any related symptoms (itching, redness, swelling, etc.). What soothes or worsens ${isMinor ? (firstName + "'s") : "your"} symptoms, if anything?`
      }
      returning={returning}
      input={
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          <FormControl sx={{ width: "100%", mb: "2rem" }}>
            <TextField
              placeholder="Answer here..."
              variant="outlined"
              color="secondary"
              sx={{ ...textAreaSx, width: "100%" }}
              autoComplete="off"
              multiline
              rows={5}
              value={other}
              onChange={(e) => setOther(e.target.value)}
            />
            {errorMessage && (
              <FormHelperText error sx={{ fontSize: "0.85rem", mt: 1 }}>
                {errorMessage}
              </FormHelperText>
            )}
          </FormControl>
          <ContinueButton
            onClick={handleSubmit}
            title={processing ? "Processing..." : submitButton}
            disabled={processing || (user.concern === "Other / unsure" && other.trim() === "")}
            selected={!processing}
          />
        </Box>
      }
    />
  );
}
